import type { AccessChallenge } from "@shared/access.types";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthCard, CodeInput } from "@/components/AuthCard";
import { Button } from "@/components/ds/Button";
import { ACCESS_MESSAGES, AccessError, api } from "@/lib/api";
import { copy } from "@/lib/copy";

/**
 * Le parcours d'entrée d'un partage : ouvrir le lien, recevoir un code, le saisir.
 *
 * L'écran ne montre jamais l'email complet du destinataire : un lien transféré ne
 * doit rien apprendre à celui qui le reçoit.
 */
export function AccessPage() {
	const { token = "" } = useParams();
	const navigate = useNavigate();
	const [challenge, setChallenge] = useState<AccessChallenge | null>(null);
	const [code, setCode] = useState("");
	const [message, setMessage] = useState<string | null>(null);
	const [busy, setBusy] = useState(true);

	useEffect(() => {
		let cancelled = false;
		api
			.requestCode(token)
			.then((result) => {
				if (!cancelled) setChallenge(result);
			})
			.catch((error: unknown) => {
				// Sans repli, une panne réseau laissait `message` à null et l'écran rendait
				// une alerte VIDE : l'entreprise voyait un cadre sans un mot dedans.
				if (cancelled) return;
				setMessage(error instanceof AccessError ? ACCESS_MESSAGES[error.code] : copy.share.failed);
			})
			.finally(() => {
				if (!cancelled) setBusy(false);
			});
		return () => {
			cancelled = true;
		};
	}, [token]);

	async function submit(event: FormEvent) {
		event.preventDefault();
		setBusy(true);
		setMessage(null);
		try {
			await api.verifyCode(token, code);
			navigate("/partage", { replace: true });
		} catch (error) {
			setMessage(
				error instanceof AccessError ? ACCESS_MESSAGES[error.code] : copy.deposit.sendFailed,
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<AuthCard eyebrow={copy.share.eyebrow} title={copy.share.title}>
			{challenge ? (
				<>
					<p className="mb-6 rounded-field bg-mg-subtle px-4 py-3 t-body text-mg-heading">
						{copy.share.codeSent(challenge.maskedEmail)}
					</p>
					<form onSubmit={submit} className="flex flex-col gap-5">
						<CodeInput
							value={code}
							onChange={setCode}
							label={copy.share.codeLabel}
							error={message}
						/>
						<Button tone="primary" full type="submit" disabled={busy || code.length !== 6}>
							{copy.share.submit}
						</Button>
					</form>
				</>
			) : busy ? (
				<p className="t-body text-mg-secondary">{copy.share.checking}</p>
			) : (
				<p role="alert" className="t-body text-mg-heading">
					{message}
				</p>
			)}
		</AuthCard>
	);
}
